import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import {
  sessionsClient,
  type FeedbackEventRequest,
  type FeedbackRating,
  type FeedbackReasonCode,
  type SessionEventResult,
} from '../../../api-clients/sessions';
import { useUserpilotTracking } from '../../../hooks';
import { TRACK_EVENTS } from '../../../userpilot';

export type NoteFeedbackView = 'collapsed' | 'panel' | 'confirmed';

export const NOTE_FEEDBACK_CONFIRMATION = 'We appreciate your feedback!';
export const NOTE_FEEDBACK_LIMIT_MESSAGE =
  'Feedback limit reached for this note';
export const NOTE_FEEDBACK_SAVE_FAILED = "Couldn't save your feedback";

interface FeedbackAction {
  kind: 'rate' | 'submit';
  /** New for each user action; a retry of the action resends it unchanged. */
  feedbackId: string;
  rating: FeedbackRating;
  reasonCodes?: FeedbackReasonCode[];
  comment?: string;
}

export interface NoteFeedbackFailure {
  action: FeedbackAction;
  /** False for a 409: the same request would be rejected again. */
  retryable: boolean;
}

/** The open panel's picked reasons and typed comment, not yet sent. */
export interface NoteFeedbackDraft {
  reasonCodes: FeedbackReasonCode[];
  comment: string;
}

interface NoteFeedbackState {
  view: NoteFeedbackView;
  /** The rating the server last accepted for this note. */
  rating: FeedbackRating | null;
  pending: number;
  failure: NoteFeedbackFailure | null;
  /** The API reported the note's feedback cap, so nothing more can be saved. */
  limitReached: boolean;
  draft: NoteFeedbackDraft;
  /** Text for the polite live region. */
  announcement: string;
}

const EMPTY_DRAFT: NoteFeedbackDraft = { reasonCodes: [], comment: '' };

const INITIAL_STATE: NoteFeedbackState = {
  view: 'collapsed',
  rating: null,
  pending: 0,
  failure: null,
  limitReached: false,
  draft: EMPTY_DRAFT,
  announcement: '',
};

// Keyed by documentId: switching tabs unmounts the footer but keeps a note's
// state, and a regenerated note (new documentId) starts from the collapsed row.
// The draft and a failed submit hold the typed comment, so both are cleared on
// a successful Submit, on Skip/X, and when another note's footer mounts.
const useNoteFeedbackStore = create<Record<string, NoteFeedbackState>>(
  () => ({})
);

const readState = (documentId: string): NoteFeedbackState =>
  useNoteFeedbackStore.getState()[documentId] ?? INITIAL_STATE;

const updateState = (
  documentId: string,
  update: (previous: NoteFeedbackState) => Partial<NoteFeedbackState>
): void => {
  const previous = readState(documentId);
  useNoteFeedbackStore.setState({
    [documentId]: { ...previous, ...update(previous) },
  });
};

/** Drops the draft and failure (and with them any comment) of every other note. */
const clearOtherNotes = (documentId: string): void => {
  const cleared: Record<string, NoteFeedbackState> = {};
  for (const [id, state] of Object.entries(useNoteFeedbackStore.getState())) {
    if (id !== documentId && (state.failure || state.draft !== EMPTY_DRAFT)) {
      cleared[id] = { ...state, draft: EMPTY_DRAFT, failure: null };
    }
  }
  if (Object.keys(cleared).length > 0) useNoteFeedbackStore.setState(cleared);
};

// One request at a time per note, sent in the order the user acted.
const queues = new Map<string, Promise<SessionEventResult>>();

const enqueue = (
  documentId: string,
  task: () => Promise<SessionEventResult>
): Promise<SessionEventResult> => {
  const run = (queues.get(documentId) ?? Promise.resolve({ ok: true })).then(
    task
  );
  queues.set(documentId, run);
  return run;
};

const toRequest = (
  documentId: string,
  action: FeedbackAction
): FeedbackEventRequest => ({
  type: 'feedback',
  kind: action.kind === 'rate' ? 'rating' : 'enrichment',
  feedbackId: action.feedbackId,
  documentId,
  rating: action.rating,
  ...(action.reasonCodes && { reasonCodes: action.reasonCodes }),
  ...(action.comment && { comment: action.comment }),
});

export function useNoteFeedback({
  sessionId,
  documentId,
}: {
  sessionId: string;
  documentId: string;
}) {
  const state = useNoteFeedbackStore((all) => all[documentId] ?? INITIAL_STATE);
  const { trackEvent } = useUserpilotTracking();

  useEffect(() => {
    clearOtherNotes(documentId);
  }, [documentId]);

  const send = useCallback(
    async (action: FeedbackAction) => {
      updateState(documentId, (previous) => ({
        pending: previous.pending + 1,
        failure: null,
        announcement: '',
      }));

      const result = await enqueue(documentId, () =>
        sessionsClient.postSessionEvent(
          sessionId,
          toRequest(documentId, action)
        )
      );

      if (!result.ok) {
        const limitReached =
          result.status === 409 && result.code === 'feedback_limit_reached';
        updateState(documentId, (previous) => ({
          pending: previous.pending - 1,
          failure: { action, retryable: result.status !== 409 },
          limitReached: previous.limitReached || limitReached,
          announcement: limitReached
            ? NOTE_FEEDBACK_LIMIT_MESSAGE
            : NOTE_FEEDBACK_SAVE_FAILED,
        }));
        return;
      }

      const ids = { sessionId, documentId, feedbackId: action.feedbackId };
      if (action.kind === 'rate') {
        const previousRating = readState(documentId).rating;
        updateState(documentId, (previous) => ({
          pending: previous.pending - 1,
          rating: action.rating,
          view: 'panel',
          announcement: 'Rating saved',
        }));
        if (previousRating && previousRating !== action.rating) {
          trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_RATING_CHANGED, {
            ...ids,
            rating: action.rating,
            previousRating,
          });
        } else {
          trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_RATING_SELECTED, {
            ...ids,
            rating: action.rating,
          });
        }
        trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_PANEL_OPENED, {
          sessionId,
          documentId,
          rating: action.rating,
        });
        return;
      }

      updateState(documentId, (previous) => ({
        pending: previous.pending - 1,
        view: 'confirmed',
        draft: EMPTY_DRAFT,
        announcement: NOTE_FEEDBACK_CONFIRMATION,
      }));
      trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_ENRICHMENT_SUBMITTED, {
        ...ids,
        rating: action.rating,
        reasonCodes: action.reasonCodes ?? [],
        hasComment: Boolean(action.comment),
      });
      trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_CONFIRMATION_SHOWN, {
        sessionId,
        documentId,
        rating: action.rating,
      });
    },
    [sessionId, documentId, trackEvent]
  );

  /** Saves a new rating; the rating already saved just reopens its panel. */
  const rate = useCallback(
    (rating: FeedbackRating) => {
      const { rating: saved, failure } = readState(documentId);
      // The thumb whose save failed resends that same action.
      if (failure?.action.kind === 'rate' && failure.action.rating === rating) {
        void send(failure.action);
        return;
      }
      if (saved === rating) {
        updateState(documentId, () => ({ view: 'panel', failure: null }));
        trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_PANEL_OPENED, {
          sessionId,
          documentId,
          rating,
        });
        return;
      }
      void send({
        kind: 'rate',
        feedbackId: window.crypto.randomUUID(),
        rating,
      });
    },
    [documentId, sessionId, send, trackEvent]
  );

  /** Adds the draft's reasons and/or comment to the saved rating as a new entry. */
  const submit = useCallback(() => {
    const { rating, failure, draft } = readState(documentId);
    // The panel is locked while a submit failure shows, so this resends exactly it.
    if (failure?.action.kind === 'submit') {
      void send(failure.action);
      return;
    }
    if (!rating) return;
    const comment = draft.comment.trim();
    void send({
      kind: 'submit',
      feedbackId: window.crypto.randomUUID(),
      rating,
      ...(draft.reasonCodes.length > 0 && { reasonCodes: draft.reasonCodes }),
      ...(comment && { comment }),
    });
  }, [documentId, send]);

  /** Resends the failed action with its original feedbackId. */
  const retry = useCallback(() => {
    const { failure } = readState(documentId);
    if (failure) void send(failure.action);
  }, [documentId, send]);

  /** Skip or X: the rating is already saved, so nothing is sent. */
  const dismiss = useCallback(
    (method: 'skip' | 'close') => {
      const { pending, rating: saved } = readState(documentId);
      // A save in flight may still fail; the Confirmed row must not hide that.
      if (pending > 0) return;
      const rating = saved ?? undefined;
      updateState(documentId, () => ({
        view: 'confirmed',
        failure: null,
        draft: EMPTY_DRAFT,
        announcement: NOTE_FEEDBACK_CONFIRMATION,
      }));
      trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_DISMISSED, {
        sessionId,
        documentId,
        rating,
        dismissMethod: method,
      });
      trackEvent(TRACK_EVENTS.NOTE_FEEDBACK_CONFIRMATION_SHOWN, {
        sessionId,
        documentId,
        rating,
      });
    },
    [documentId, sessionId, trackEvent]
  );

  /** From the Confirmed row back to the thumbs, so the rating can change. */
  const reopen = useCallback(() => {
    updateState(documentId, () => ({ view: 'collapsed', announcement: '' }));
  }, [documentId]);

  const toggleReason = useCallback(
    (code: FeedbackReasonCode) => {
      updateState(documentId, ({ draft }) => ({
        draft: {
          ...draft,
          reasonCodes: draft.reasonCodes.includes(code)
            ? draft.reasonCodes.filter((selected) => selected !== code)
            : [...draft.reasonCodes, code],
        },
      }));
    },
    [documentId]
  );

  const setComment = useCallback(
    (comment: string) => {
      updateState(documentId, ({ draft }) => ({
        draft: { ...draft, comment },
      }));
    },
    [documentId]
  );

  return {
    view: state.view,
    rating: state.rating,
    saving: state.pending > 0,
    failure: state.failure,
    limitReached: state.limitReached,
    draft: state.draft,
    announcement: state.announcement,
    rate,
    submit,
    retry,
    dismiss,
    reopen,
    toggleReason,
    setComment,
  };
}
